import React, { useEffect, useState, useRef } from "react";
import "../../css/CategoryPage.css";
import axios from "axios";
import Footer from "../../components/footer/Footer";
import { useAuth } from "../../context/auth";
import AdminHeader from "../../components/header/AdminHeader";
import SearchIcon from "@mui/icons-material/Search";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CancelIcon from "@mui/icons-material/Cancel";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import { toast } from "react-hot-toast";

const PastOrders = () => {
  const [auth] = useAuth();
  const [allOrders, setAllOrders] = useState([]);
  const [displayedOrders, setDisplayedOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'shipped', 'cancelled'
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  const topRef = useRef(null);
  const observerTarget = useRef(null);
  const itemsPerLoad = 10; // Load 10 orders at a time

  const apiUrl = `${process.env.REACT_APP_API}`;

  useEffect(() => {
    if (auth.token) {
      getOrders();
    }
  }, [auth.token]);

  const getOrders = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(`${apiUrl}/all-orders`);

      // Filter only shipped, cancelled, and refunded orders
      const pastOrders = data.filter(
          (order) => order.status === "Shipped" || order.status === "Cancelled" || order.status === "Refunded"
      );

      // Sort by date (newest first)
      const sortedOrders = pastOrders.sort((a, b) =>
          new Date(b.createdAt) - new Date(a.createdAt)
      );

      setAllOrders(sortedOrders);
      setDisplayedOrders(sortedOrders.slice(0, itemsPerLoad));
      setHasMore(sortedOrders.length > itemsPerLoad);
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  };

  // Filter orders based on search and status
  const getFilteredOrders = () => {
    let filtered = [...allOrders];

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((order) =>
          order.status.toLowerCase() === statusFilter
      );
    }

    // Apply search filter
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((order) => {
        const buyerName = order.isGuestOrder
          ? `${order.guestInfo.firstName} ${order.guestInfo.lastName}`.toLowerCase()
          : `${order.buyer.name.first} ${order.buyer.name.last}`.toLowerCase();
        const email = order.isGuestOrder
          ? order.guestInfo.email.toLowerCase()
          : order.buyer.email.toLowerCase();
        const orderDate = order.createdAt.substring(0, 10);
        const trackingNumber = order.trackingNumber ? order.trackingNumber.toLowerCase() : "";

        return (
            buyerName.includes(query) ||
            email.includes(query) ||
            orderDate.includes(query) ||
            trackingNumber.includes(query)
        );
      });
    }

    return filtered;
  };

  const filteredOrders = getFilteredOrders();

  // Load more orders for infinite scroll
  const loadMoreOrders = () => {
    const currentLength = displayedOrders.length;
    const filtered = getFilteredOrders();
    const nextOrders = filtered.slice(currentLength, currentLength + itemsPerLoad);

    setDisplayedOrders([...displayedOrders, ...nextOrders]);
    setHasMore(currentLength + nextOrders.length < filtered.length);
  };

  // Reset displayed orders when filters change
  useEffect(() => {
    const filtered = getFilteredOrders();
    setDisplayedOrders(filtered.slice(0, itemsPerLoad));
    setHasMore(filtered.length > itemsPerLoad);
  }, [searchQuery, statusFilter, allOrders]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !isLoading) {
            loadMoreOrders();
          }
        },
        { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, displayedOrders, searchQuery, statusFilter]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
  };

  const handleRefund = async (orderId) => {
    if (!window.confirm("Are you sure you want to refund this order? This will send a confirmation email to the customer.")) {
      return;
    }

    try {
      const { data } = await axios.put(`${apiUrl}/refund-order/${orderId}`);

      if (data.success) {
        toast.success("Order refunded successfully! Customer has been notified via email.");
        // Refresh orders
        getOrders();
      }
    } catch (error) {
      console.error("Refund error:", error);
      toast.error(error.response && error.response.data && error.response.data.message || "Failed to process refund");
    }
  };

  return (
      <>
        <div ref={topRef}></div>
        <AdminHeader />
        <div className="container my-5">
          <h1 className="text-center mb-4" style={{ color: "#002E6A", fontWeight: "600" }}>
            Past Orders
          </h1>

          {/* Filter Section */}
          <div className="card shadow-sm mb-4 p-3">
            <div className="row g-3">
              {/* Search Bar */}
              <div className="col-md-8">
                <div className="input-group">
                <span className="input-group-text">
                  <SearchIcon />
                </span>
                  <input
                      type="text"
                      className="form-control"
                      placeholder="Search by customer name, email, date, or tracking number..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="col-md-4">
                <div className="btn-group w-100" role="group">
                  <button
                      className={`btn ${statusFilter === "all" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => handleStatusFilterChange("all")}
                  >
                    All
                  </button>
                  <button
                      className={`btn ${statusFilter === "shipped" ? "btn-success" : "btn-outline-success"}`}
                      onClick={() => handleStatusFilterChange("shipped")}
                  >
                    Shipped
                  </button>
                  <button
                      className={`btn ${statusFilter === "cancelled" ? "btn-danger" : "btn-outline-danger"}`}
                      onClick={() => handleStatusFilterChange("cancelled")}
                  >
                    Cancelled
                  </button>
                  <button
                      className={`btn ${statusFilter === "refunded" ? "btn-warning" : "btn-outline-warning"}`}
                      onClick={() => handleStatusFilterChange("refunded")}
                  >
                    Refunded
                  </button>
                </div>
              </div>
            </div>

            {/* Results Count */}
            <div className="mt-2">
              <small className="text-muted">
                Showing {displayedOrders.length} of {filteredOrders.length} orders
              </small>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
              <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading orders...</p>
              </div>
          )}

          {/* No Orders */}
          {!isLoading && filteredOrders.length === 0 && (
              <div className="text-center my-5">
                <h4>No past orders found</h4>
                <p className="text-muted">
                  {searchQuery || statusFilter !== "all"
                      ? "Try adjusting your search or filters"
                      : "There are no past orders yet"}
                </p>
              </div>
          )}

          {/* Orders List */}
          {!isLoading && displayedOrders.length > 0 && (
              <>
                {displayedOrders.map((order) => (
                    <div key={order._id} className="card shadow-sm mb-4">
                      <div className="card-header" style={{
                        backgroundColor: order.status === "Shipped" ? "#d4edda" :
                                       order.status === "Refunded" ? "#fff3cd" : "#f8d7da"
                      }}>
                        <div className="row align-items-center">
                          <div className="col-md-6">
                            <h5 className="mb-0" style={{
                              color: order.status === "Shipped" ? "#155724" :
                                     order.status === "Refunded" ? "#856404" : "#721c24"
                            }}>
                              {order.status === "Shipped" ? (
                                  <><LocalShippingIcon /> Shipped</>
                              ) : order.status === "Refunded" ? (
                                  <><MoneyOffIcon /> Refunded</>
                              ) : (
                                  <><CancelIcon /> Cancelled</>
                              )}
                            </h5>
                          </div>
                          <div className="col-md-6 text-md-end">
                            <small className="text-muted">
                              Order Date: <strong>{new Date(order.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}</strong>
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="card-body">
                        <div className="row">
                          {/* Customer Information */}
                          <div className="col-lg-6 mb-3">
                            <h6 className="border-bottom pb-2 mb-3">Customer Information</h6>
                            {order.isGuestOrder ? (
                              <>
                                <p className="mb-2">
                                  <strong>Name:</strong> {order.guestInfo.firstName} {order.guestInfo.lastName}
                                  <span className="badge bg-info ms-2">Guest Order</span>
                                </p>
                                <p className="mb-2">
                                  <EmailIcon fontSize="small" /> <strong>Email:</strong> {order.guestInfo.email}
                                </p>
                                <p className="mb-2">
                                  <PhoneIcon fontSize="small" /> <strong>Phone:</strong> {order.guestInfo.phone}
                                </p>
                                <p className="mb-2">
                                  <LocationOnIcon fontSize="small" /> <strong>Address:</strong><br />
                                  <span className="ms-4">
                                    {order.guestInfo.address.street1}
                                    {order.guestInfo.address.street2 && `, ${order.guestInfo.address.street2}`}
                                    <br />
                                    {order.guestInfo.address.city}, {order.guestInfo.address.state} {order.guestInfo.address.zip}
                                  </span>
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="mb-2">
                                  <strong>Name:</strong> {order.buyer.name.first} {order.buyer.name.last}
                                </p>
                                <p className="mb-2">
                                  <EmailIcon fontSize="small" /> <strong>Email:</strong> {order.buyer.email}
                                </p>
                                <p className="mb-2">
                                  <PhoneIcon fontSize="small" /> <strong>Phone:</strong> {order.buyer.phone}
                                </p>
                                <p className="mb-2">
                                  <LocationOnIcon fontSize="small" /> <strong>Address:</strong><br />
                                  <span className="ms-4">
                                    {order.buyer.address.street1}
                                    {order.buyer.address.street2 && `, ${order.buyer.address.street2}`}
                                    <br />
                                    {order.buyer.address.city}, {order.buyer.address.state} {order.buyer.address.zip}
                                  </span>
                                </p>
                              </>
                            )}
                          </div>

                          {/* Order Details */}
                          <div className="col-lg-6 mb-3">
                            <h6 className="border-bottom pb-2 mb-3">Order Details</h6>
                            <p className="mb-2">
                              <strong>Total Amount:</strong> <span className="text-success fs-5">${order.total}</span>
                            </p>
                            {order.status === "Shipped" && order.trackingNumber && (
                                <p className="mb-2">
                                  <strong>Tracking Number:</strong>{" "}
                                  <a
                                      href={`https://tools.usps.com/go/TrackConfirmAction?tRef=fullpage&tLc=3&text28777=&tLabels=${order.trackingNumber}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-decoration-none"
                                  >
                                    {order.trackingNumber} 🔗
                                  </a>
                                </p>
                            )}

                            {/* Refund Button - Only show for Shipped or Cancelled orders */}
                            {(order.status === "Shipped" || order.status === "Canceled") && (
                                <div className="mt-3">
                                  <button
                                      className="btn btn-warning btn-sm"
                                      onClick={() => handleRefund(order._id)}
                                  >
                                    <MoneyOffIcon fontSize="small" /> Process Refund
                                  </button>
                                </div>
                            )}
                          </div>
                        </div>

                        {/* Products */}
                        <div className="mt-3">
                          <h6 className="border-bottom pb-2 mb-3">Products ({order.products.length})</h6>
                          <div className="row g-3">
                            {order.products.map((p) => (
                                p.product && (
                                    <div key={p._id} className="col-12">
                                      <div className="card">
                                        <div className="card-body">
                                          <div className="row align-items-center">
                                            <div className="col-md-2 text-center">
                                              <img
                                                  src={p.product.images.front}
                                                  alt={p.product.productName}
                                                  className="img-fluid rounded"
                                                  style={{
                                                    maxWidth: "100px",
                                                    maxHeight: "100px",
                                                    objectFit: "contain",
                                                  }}
                                                  loading="lazy"
                                              />
                                            </div>
                                            <div className="col-md-7">
                                              <h6 className="mb-1">{p.product.productName}</h6>
                                              <p className="mb-0 text-muted">
                                                Size: <strong>{p.selectedSize}</strong> | Qty: <strong>{p.quantity}</strong>
                                              </p>
                                            </div>
                                            <div className="col-md-3 text-md-end">
                                              <h5 className="mb-0 text-primary">
                                                ${p.product.salePrice > 0 ? p.product.salePrice : p.product.productPrice}
                                              </h5>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                )
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                ))}

                {/* Infinite Scroll Observer */}
                <div ref={observerTarget} className="text-center my-4">
                  {hasMore && (
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading more...</span>
                      </div>
                  )}
                  {!hasMore && displayedOrders.length > 0 && (
                      <p className="text-muted">✓ All {filteredOrders.length} orders loaded</p>
                  )}
                </div>
              </>
          )}
        </div>
        <Footer />

        <style jsx>{`
        .card {
          transition: transform 0.2s, box-shadow 0.2s;
        }
        
        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
        }

        .input-group-text {
          background-color: white;
        }
      `}</style>
      </>
  );
};

export default PastOrders;